/**
 * Ritual Member Invite Notification Component
 * Displays a notification for ritual member invites with Accept/Reject buttons
 */

import { EllipsisVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useAcceptRitualMemberInvite,
  useRejectRitualMemberInvite,
} from "@/features/rituals/hooks/useRitualInvites"
import { getRitualTypeLabel } from "@/features/rituals/types"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface RitualMemberInviteNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
}

interface RitualMemberInviteNotificationData {
  ritualId?: string
  ritualName?: string
  inviteId?: string
  inviterName?: string
  inviterUsername?: string
  ritualCoverImageUrl?: string | null
  inviteNote?: string
  actionTaken?: "accepted" | "declined" | "withdrawn"
  actionTakenAt?: string
  isPaid?: boolean
  fee?: number | null
  duration?: string
  ritualType?: string
  durationType?: string
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function RitualMemberInviteNotification({
  notification,
  onRead,
  onUnread,
}: RitualMemberInviteNotificationProps) {
  const navigate = useNavigate()

  const data = notification.data as RitualMemberInviteNotificationData

  const ritualId = data.ritualId
  const inviteId = data.inviteId || notification.entityId
  const actionTaken = data.actionTaken

  const { acceptRitualMemberInvite, isAccepting } = useAcceptRitualMemberInvite(ritualId || "")
  const { rejectRitualMemberInvite, isRejecting } = useRejectRitualMemberInvite(ritualId || "")

  if (!notification.triggeredByUser || !notification.data || !inviteId || !ritualId) {
    return null
  }

  const inviter = notification.triggeredByUser

  const ritualName = data.ritualName || "a ritual"
  const inviterUsername = data.inviterUsername
  const ritualCoverImageUrl = data.ritualCoverImageUrl
  const isPaid = data.isPaid
  const fee = data.fee
  const duration = data.duration
  const durationType = data.durationType
  const ritualType = data.ritualType
  const isRead = notification.isRead

  const displayImageUrl = getUserDisplayImage(inviter)
  const displayName = inviter.name || "Someone"

  const isProcessing = isAccepting || isRejecting
  const relativeTime = formatNotificationTime(notification.createdAt)

  const priceDisplay = isPaid && fee ? `$${fee}` : "Free"
  const ritualTypeLabel = ritualType ? getRitualTypeLabel(ritualType) : ""

  function formatRitualDuration(dur: string | undefined, durType: string | undefined): string {
    if (!dur) return ""
    const mins = parseInt(dur, 10)
    if (!durType || durType === "minutes") return `${mins}min`
    if (durType === "hours") {
      const hrs = Math.floor(mins / 60)
      const remainingMins = mins % 60
      return remainingMins > 0 ? `${hrs}hrs ${remainingMins}min` : `${hrs}hrs`
    }
    if (durType === "days") {
      const days = Math.floor(mins / 1440)
      return `${days}days`
    }
    return `${mins}min`
  }

  const formattedDuration = formatRitualDuration(duration, durationType)

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/profile/${inviter.id}`)
    if (!isRead) {
      onRead?.()
    }
  }

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/rituals/${ritualId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation()
    acceptRitualMemberInvite(inviteId)
    onRead?.()
  }

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation()
    rejectRitualMemberInvite(inviteId)
    onRead?.()
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
    >
      {/* Header Row: Avatar, Name, Username, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* User avatar */}
          <div
            className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleAvatarClick}
          >
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {inviterUsername && (
                <span className="text-stone text-[14px]">@{inviterUsername}</span>
              )}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              <span className="font-semibold">{displayName}</span> invited you to join ritual{" "}
              <span className="font-medium cursor-pointer" onClick={handleRitualClick}>
                {ritualName}
              </span>{" "}
              as a member.
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-1 -right-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Invite Note - if present */}
      {data.inviteNote && (
        <div className="mt-2 mx-0 px-3 py-2 bg-stone/10 rounded text-xs italic text-primary/80">
          {data.inviteNote}
        </div>
      )}

      {/* Action Buttons or Status */}
      {actionTaken ? (
        <div className="text-[11px] text-stone font-medium px-15">
          {actionTaken === "accepted"
            ? "Accepted the invite"
            : actionTaken === "declined"
              ? "Declined the invite"
              : "Invite was withdrawn"}
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-2 px-15" onClick={e => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline"
            className="border-1 border-white text-base px-6 uppercase text-[14px] leading-[20px]"
            onClick={handleReject}
            disabled={isProcessing}
          >
            Decline
          </Button>
          <Button
            size="sm"
            variant="primary"
            className="bg-white text-base px-6 uppercase text-[14px] leading-[20px]"
            onClick={handleAccept}
            disabled={isProcessing}
          >
            Accept
          </Button>
        </div>
      )}

      {/* Ritual Card */}
      <div
        className="relative mt-2 h-[357.5px] mx-auto md:w-[520px] rounded-2xl overflow-hidden cursor-pointer border border-card"
        onClick={handleRitualClick}
      >
        {ritualCoverImageUrl ? (
          <img src={ritualCoverImageUrl} alt={ritualName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-card flex items-center justify-center">
            <span className="text-stone text-sm">No cover image</span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-[20px] bg-gradient-to-t from-[rgba(0, 0, 0, 0.83)] to-transparent">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[20px] text-white truncate leading-[20px]">
              {ritualName}
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`text-white text-[13px] px-2 py-1 rounded-sm leading-[20px] ${isPaid ? "bg-brand-terracotta" : "bg-brand-olive"}`}
              >
                {priceDisplay}
              </span>
              <span className="text-white/80 text-[13px] leading-[20px]">{ritualTypeLabel}</span>
              <span className="text-white/60 text-[13px] leading-[20px]">{formattedDuration}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
